import React from 'react';
import { motion } from 'framer-motion';
import { FaTruck, FaRocket, FaClock, FaCheckCircle } from 'react-icons/fa';
import { cardFloat } from "../animations/animations";

const ShippingOptions = ({ selected, onSelect, cartTotal, methods = [], freeShippingThreshold = 2000 }) => {
    // Use methods from props or show loading state
    const shippingMethods = methods.length > 0 ? methods : [
        {
            id: 'standard',
            name: 'Standard Delivery',
            description: '4-7 business days',
            price: 49,
            icon: FaTruck,
            estimatedDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        },
        {
            id: 'express',
            name: 'Express Delivery',
            description: '2-3 business days',
            price: 99,
            icon: FaRocket,
            estimatedDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)
        },
        {
            id: 'overnight',
            name: 'Overnight',
            description: 'Next business day',
            price: 199,
            icon: FaClock,
            estimatedDate: new Date(Date.now() + 24 * 60 * 60 * 1000)
        }
    ];

    const formatDate = (date) => {
        if (!date) return 'Estimate delivery date';

        if (typeof date === 'string') {
            date = new Date(date);
        }

        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'short',
            day: 'numeric'
        });
    };

    const getIconForMethod = (methodId) => {
        switch(methodId) {
            case 'standard': return FaTruck;
            case 'express': return FaRocket;
            case 'overnight': return FaClock;
            default: return FaTruck;
        }
    };

    const getFeatures = (methodId) => {
        switch(methodId) {
            case 'standard':
                return ['Tracking included', 'Contactless delivery'];
            case 'express':
                return ['Priority handling', 'Real-time tracking'];
            case 'overnight':
                return ['Guaranteed delivery', 'Insurance included'];
            default:
                return ['Tracking included', 'Contactless delivery'];
        }
    };

    const getEstimatedDate = (methodId) => {
        const today = new Date();
        switch(methodId) {
            case 'standard':
                return new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
            case 'express':
                return new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000);
            case 'overnight':
                return new Date(today.getTime() + 24 * 60 * 60 * 1000);
            default:
                return new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
        }
    };

    // If no methods are loaded yet, show loading state
    if (methods.length === 0) {
        return (
            <motion.div
                variants={cardFloat}
                className="space-y-4"
            >
                {[1, 2, 3].map((item) => (
                    <div
                        key={item}
                        className="w-full p-6 rounded-2xl border border-nude/30 bg-gradient-to-r from-champagne/5 to-transparent animate-pulse"
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-4 flex-1">
                                <div className="w-12 h-12 rounded-full bg-nude/30"></div>
                                <div className="flex-1 space-y-2">
                                    <div className="h-4 bg-nude/30 rounded w-1/3"></div>
                                    <div className="h-3 bg-nude/30 rounded w-1/2"></div>
                                    <div className="flex gap-2">
                                        <div className="h-3 bg-nude/30 rounded w-16"></div>
                                        <div className="h-3 bg-nude/30 rounded w-20"></div>
                                    </div>
                                    <div className="h-3 bg-nude/30 rounded w-2/3"></div>
                                </div>
                            </div>
                            <div className="h-6 bg-nude/30 rounded w-16"></div>
                        </div>
                    </div>
                ))}
            </motion.div>
        );
    }

    return (
        <motion.div
            variants={cardFloat}
            className="space-y-4"
        >
            {shippingMethods.map((method, index) => {
                // Calculate price - free shipping if cart meets threshold
                const price = cartTotal >= freeShippingThreshold ? 0 : (method.price || 0);
                const Icon = getIconForMethod(method.id);
                const features = method.features || getFeatures(method.id);
                const estimatedDate = method.estimatedDate || method.days
                    ? new Date(Date.now() + (method.days || 7) * 24 * 60 * 60 * 1000)
                    : getEstimatedDate(method.id);

                return (
                    <motion.div
                        key={method.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ y: -2 }}
                    >
                        <button
                            onClick={() => onSelect(method.id)}
                            className={`
                                w-full p-6 rounded-2xl text-left transition-all duration-300
                                ${selected === method.id
                                ? 'border-2 border-champagne bg-gradient-to-r from-champagne/5 to-transparent card-selected'
                                : 'border border-nude hover:border-champagne/50 hover:bg-nude/5'
                            }
                            `}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-4 flex-1">
                                    <div className={`
                                        w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
                                        ${selected === method.id
                                        ? 'bg-gradient-to-br from-champagne to-champagne/80 text-white'
                                        : 'bg-nude/30 text-charcoal/50'
                                    }
                                    `}>
                                        <Icon className="w-5 h-5" />
                                    </div>

                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <h3 className="font-medium text-charcoal">{method.name}</h3>
                                            {selected === method.id && (
                                                <FaCheckCircle className="w-4 h-4 text-sage" />
                                            )}
                                        </div>

                                        <p className="text-sm text-charcoal/60 mb-3">
                                            {method.description || `${method.days || '4-7'} business days`}
                                        </p>

                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {features.map((feature, idx) => (
                                                <span
                                                    key={idx}
                                                    className="text-xs px-2 py-1 bg-nude/20 rounded-full text-charcoal/60"
                                                >
                                                    {feature}
                                                </span>
                                            ))}
                                        </div>

                                        <p className="text-sm text-charcoal/50">
                                            Estimated delivery: <span className="font-medium text-charcoal">
                                                {formatDate(estimatedDate)}
                                            </span>
                                        </p>
                                    </div>
                                </div>

                                <div className="text-right">
                                    <div className="text-2xl font-light text-charcoal mb-1">
                                        {price === 0 ? 'FREE' : `₹${price.toFixed(2)}`}
                                    </div>
                                    {cartTotal >= freeShippingThreshold && price === 0 && (
                                        <p className="text-xs text-sage">
                                            Free shipping unlocked! 🎉
                                        </p>
                                    )}
                                    {method.originalPrice && method.originalPrice > price && (
                                        <p className="text-xs text-charcoal/40 line-through">
                                            ₹{method.originalPrice.toFixed(2)}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </button>
                    </motion.div>
                );
            })}

            {/* Shipping Note */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="p-4 bg-gradient-to-r from-sage/5 to-transparent rounded-xl"
            >
                <div className="flex items-center gap-3">
                    <FaCheckCircle className="w-4 h-4 text-sage" />
                    <p className="text-sm text-charcoal/60">
                        All orders are shipped with carbon-neutral delivery and eco-friendly packaging.
                        {cartTotal < freeShippingThreshold && (
                            <span className="font-medium text-charcoal ml-1">
                                Add ₹{(freeShippingThreshold - cartTotal).toFixed(2)} more for free shipping!
                            </span>
                        )}
                    </p>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default ShippingOptions;